import { store } from './core/store.js';
import { Storage } from './core/storage.js';
import { refs } from './core/refs.js';
import { initWasm, getSandboxUrl } from './core/wasm.js';
import { ConsoleView } from './features/console-view.js';
import { ScratchPad } from './features/scratchpad.js';
import { configureExport, configureRunner, runCode, RUN_TRIGGERS, exportCode, registerExportDialog, fallbackToSameOriginPreview, resetTransport, previewBridge, finishPreviewTimingById, handlePreviewRenderComplete, handlePreviewScroll, isJsPending } from './features/runner.js';
import { configureLiveReflection, refreshJsPendingState, registerLiveReflection, setLive, triggerAllHighlights } from './features/live-reflection.js';
import { configureTemplateLoader, loadTemplate, resetFiddle, setTemplateSelectValue, populateTemplateSelects } from './features/template-loader.js';
import { importFiles, MAX_BYTES, formatBytes } from './features/file-import.js';
import { populateThemeControls, registerThemeFeature, setTheme } from './features/theme.js';
import { registerMenuFeature, closeMenu } from './features/menu.js';
import { registerResponsiveFeature, isMobile, applyResponsiveMode } from './features/responsive.js';
import { closeContextMenu, registerContextMenu } from './features/context-menu.js';
import { handlePreviewContextMenuMessage, initPreviewPresets } from './features/preview-presets.js';
import { registerEditorTools } from './features/editor-tools.js';
const registerShareFeature = () => {};
import { handlePreviewInspectResult, registerPreviewInspector, disableInspector } from './features/preview-inspector.js';
import { panelSystem } from './ui/panels.js';
import { Modal } from './core/modal.js';
import { registerTooltipFeature } from './features/tooltip.js';
import { initCustomSelects, syncCustomSelect } from './features/custom-select.js';
import { initAnalytics, captureAnalyticsEvent, captureAnalyticsError } from './core/analytics.js';

const STARTUP_TIMINGS = [];
// Optional startup phases that failed; the app keeps running without them.
const DEGRADED_STARTUP_PHASES = [];

if (typeof window !== 'undefined') {
  window.__FRONT_MY_END_STARTUP_TIMINGS__ = STARTUP_TIMINGS;
}

function startupNow() {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

function recordStartupPhase(name, startedAt) {
  STARTUP_TIMINGS.push({
    name,
    durationMs: Math.round((startupNow() - startedAt) * 100) / 100
  });
}

/**
 * Runs one startup phase. A critical phase that throws aborts startup (the
 * caller shows the startup-failure screen); an optional phase is recorded as
 * degraded and startup continues without it.
 */
async function runStartupPhase(name, task, { optional = false } = {}) {
  const startedAt = startupNow();
  try {
    return await task();
  } catch (error) {
    if (!optional) {
      if (error && typeof error === 'object' && !error.startupPhase) error.startupPhase = name;
      throw error;
    }
    recordDegradedPhase(name, error);
    return undefined;
  } finally {
    recordStartupPhase(name, startedAt);
  }
}

function recordDegradedPhase(name, error) {
  DEGRADED_STARTUP_PHASES.push(name);
  console.error(`[Front-my-End] Startup step "${name}" failed; continuing without it.`, error);
  captureAnalyticsError(error, { startup_phase: name, fatal: false });
}

/** Runs each feature registration independently so one failure cannot unmount the rest. */
function registerFeatures(registrations) {
  for (const [name, register] of registrations) {
    try {
      register();
    } catch (error) {
      recordDegradedPhase(name, error);
    }
  }
}

/**
 * Visible failure state for a startup error the app cannot recover from.
 * Saved work is untouched: storage sync never starts after a failed restore.
 */
function showStartupFailure(error) {
  const phase = error && error.startupPhase ? ` during "${error.startupPhase}"` : '';
  console.error(`[Front-my-End] Startup failed${phase}:`, error);
  try {
    initAnalytics();
    captureAnalyticsError(error instanceof Error ? error : new Error(String(error)), {
      startup_phase: (error && error.startupPhase) || 'unknown',
      fatal: true
    });
  } catch (_) {
    // Reporting must never mask the failure screen.
  }
  if (typeof document === 'undefined' || !document.body || document.getElementById('startup-failure')) return;

  const overlay = document.createElement('div');
  overlay.id = 'startup-failure';
  overlay.setAttribute('role', 'alert');
  overlay.style.cssText = 'position: fixed; inset: 0; z-index: 2147483647; display: flex; align-items: center; justify-content: center; padding: 24px; font-family: sans-serif; background: var(--surface-container-lowest, #ffffff); color: var(--on-surface, #111111);';
  const card = document.createElement('div');
  card.style.cssText = 'max-width: 420px; text-align: center; line-height: 1.5;';
  const title = document.createElement('h2');
  title.style.cssText = 'font-size: 20px; font-weight: 900; margin: 0 0 8px;';
  title.textContent = 'Front-my-End could not start';
  const body = document.createElement('p');
  body.style.cssText = 'font-size: 14px; margin: 0 0 16px;';
  body.textContent = 'Something went wrong while loading the playground. Your saved work is still stored in this browser. Reloading usually fixes this.';
  const detail = document.createElement('p');
  detail.style.cssText = 'font-size: 12px; opacity: 0.7; margin: 0 0 16px; word-break: break-word;';
  detail.textContent = `${error && error.name ? error.name : 'Error'}${phase}: ${error && error.message ? error.message : String(error)}`;
  const reload = document.createElement('button');
  reload.type = 'button';
  reload.textContent = 'Reload';
  reload.style.cssText = 'font: inherit; font-weight: 700; padding: 8px 20px; cursor: pointer;';
  reload.addEventListener('click', () => window.location.reload());
  card.append(title, body, detail, reload);
  overlay.append(card);
  document.body.append(overlay);
}

function scheduleIdleStartupPhase(name, task, timeout = 1200) {
  const run = () => {
    const startedAt = startupNow();
    try {
      task();
    } finally {
      recordStartupPhase(name, startedAt);
    }
  };

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout });
  } else {
    window.setTimeout(run, 0);
  }
}

/**
 * Front-my-End — Application Controller entry point
 */
class FiddleApp {
  constructor() {
    this.activePreviewRoute = null;

    // Feature modules
    this.console = new ConsoleView(refs.consoleOutput, refs.consoleToolbar);
    this.sandboxReadyTimer = null;
    this.scratch = new ScratchPad(
      refs.panelScratchpad,
      refs.scratchEditor,
      {
        onConsole: (m) => this.console.system(m),
        panelSystem
      }
    );
  }

  isMobile() {
    return isMobile();
  }

  setActiveMobileTab(name) {
    store.set('activeTab', name);
  }

  async start() {
    const criticalStartupStartedAt = startupNow();

    if (window.self !== window.top) {
      console.warn('[Front-my-End] Recursive frame nesting blocked.');
      document.body.innerHTML = `
        <div style="padding: 24px; font-family: sans-serif; text-align: center; color: var(--on-surface, #ef4444); background: var(--surface-container-lowest, #ffffff); height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center;">
          <h3 style="font-size: 20px; font-weight: 900; margin-bottom: 8px;">⚠️ Preview Navigation Halted</h3>
          <p style="font-size: 14px; max-width: 320px; line-height: 1.5;">Relative hash navigation redirected the preview panel to load our editor recursively. This has been safely blocked to prevent inception loops.</p>
        </div>
      `;
      return;
    }

    this.configureFeatureServices();
    this.configurePreviewBridge();
    // Optional phases degrade (the editor still works without WASM
    // highlighting, template lists or a preview); state restore is critical
    // because storage sync must never start from a half-restored workspace.
    await runStartupPhase('preview-sandbox-start', () => {
      this.startPreviewSandbox();
    }, { optional: true });

    await runStartupPhase('wasm-init', () => initWasm(), { optional: true });

    await runStartupPhase('controls-populate', () => Promise.all([
      populateTemplateSelects(),
      populateThemeControls()
    ]), { optional: true });

    await runStartupPhase('feature-registration', () => {
      // Initialize features needed before saved state/template restore can run.
      registerFeatures([
        ['scratchpad', () => this.scratch.init()],
        ['event-listeners', () => this.registerEventListeners()],
        ['live-reflection', () => registerLiveReflection()],
        ['import', () => this.registerImport()],
        ['responsive', () => registerResponsiveFeature()],
        ['theme', () => registerThemeFeature({ console: this.console })],
        ['menu', () => registerMenuFeature()],
        ['export-dialog', () => registerExportDialog()],
        ['share', () => registerShareFeature({ console: this.console })],
        ['context-menu', () => registerContextMenu({ console: this.console })],
        ['preview-presets', () => initPreviewPresets({ console: this.console })],
        ['preview-inspector', () => registerPreviewInspector({
          closeContextMenu,
          console: this.console,
          isMobile: () => this.isMobile(),
          postToPreview: (payload) => previewBridge.post(payload),
          setActiveMobileTab: (name) => this.setActiveMobileTab(name)
        })],
        ['editor-tools', () => registerEditorTools({ console: this.console })],
        ['panels', () => panelSystem.init()]
      ]);
    });

    await runStartupPhase('state-restore', () => this.restoreSavedState());

    // Initialize LocalStorage Sync
    Storage.initSync();

    // Set default view mobile tab
    store.set('activeTab', 'html');

    scheduleIdleStartupPhase('tooltip-init', registerTooltipFeature);
    scheduleIdleStartupPhase('custom-select-init', initCustomSelects);
    scheduleIdleStartupPhase('initial-highlighting', triggerAllHighlights);
    scheduleIdleStartupPhase('analytics-init', initAnalytics);
    if (DEGRADED_STARTUP_PHASES.length > 0) {
      captureAnalyticsEvent('startup_degraded', { phases: DEGRADED_STARTUP_PHASES.join(',') });
    }

    recordStartupPhase('critical-startup-total', criticalStartupStartedAt);
    const sandboxUrl = getSandboxUrl();
    console.log('Front-my-End successfully mounted.');
    this.console.system('Front-my-End successfully mounted.');
    if (DEGRADED_STARTUP_PHASES.length > 0) {
      this.console.append('error', `Some features failed to start (${DEGRADED_STARTUP_PHASES.join(', ')}). Reload the page to try again.`);
    }
    this.console.system(`Preview sandbox target: ${sandboxUrl || 'same-origin fallback'}.`);
  }

  startPreviewSandbox() {
    if (this.sandboxReadyTimer) {
      window.clearTimeout(this.sandboxReadyTimer);
      this.sandboxReadyTimer = null;
    }
    // resetTransport() also (re)points the preview frame at the sandbox, so
    // an explicit Run can restart a preview that previously fell back.
    const sandboxUrl = resetTransport();
    if (sandboxUrl && refs.previewIframe) {
      this.sandboxReadyTimer = window.setTimeout(() => {
        captureAnalyticsEvent('preview_sandbox_timeout');
        fallbackToSameOriginPreview(`Could not initialize ${sandboxUrl}.`);
      }, 1500);
      return;
    }

    fallbackToSameOriginPreview('No preview sandbox origin is configured.');
  }

  registerEventListeners() {
    // RUN
    refs.runBtn.addEventListener('click', () => { void runCode({ trigger: RUN_TRIGGERS.EXPLICIT }); });

    // EXPORT
    refs.exportBtn.addEventListener('click', () => exportCode());

    // RESET (with confirmation modal)
    const modal = refs.resetConfirmModal;
    const confirmBtn = refs.modalConfirmBtn;
    const cancelBtn = refs.modalCancelBtn;

    if (modal && confirmBtn && cancelBtn) {
      refs.resetBtn.addEventListener('click', () => modal.showModal());
      confirmBtn.addEventListener('click', () => {
        void resetFiddle();
        modal.close();
      });
      cancelBtn.addEventListener('click', () => modal.close());

      // Light-dismiss fallback for browsers without `closedby` support.
      if (!('closedBy' in HTMLDialogElement.prototype)) {
        modal.addEventListener('click', (event) => {
          if (event.target !== modal) return;
          const rect = modal.getBoundingClientRect();
          const isInside = (
            rect.top <= event.clientY && event.clientY <= rect.top + rect.height &&
            rect.left <= event.clientX && event.clientX <= rect.left + rect.width
          );
          if (!isInside) modal.close();
        });
      }
    } else {
      refs.resetBtn.addEventListener('click', () => {
        if (confirm('Reset entire playpen back to default template?')) void resetFiddle();
      });
    }

    // Generic message modal dismiss
    const msgModal = refs.messageModal;
    const msgOk = refs.messageModalOkBtn;
    if (msgModal && msgOk) {
      msgOk.addEventListener('click', () => msgModal.close());
    }

    // TEMPLATE SELECT (menu)
    const onTemplateChange = (val) => {
      void loadTemplate(val);
    };
    if (refs.menuTemplateSelect) refs.menuTemplateSelect.addEventListener('change', (e) => onTemplateChange(e.target.value));

    // THEME SELECT (menu)
    const onThemeChange = (val) => {
      void setTheme(val);
    };
    if (refs.menuThemeSelect) refs.menuThemeSelect.addEventListener('change', (e) => onThemeChange(e.target.value));

    // VIEW SELECT (top-bar + menu)
    const onViewChange = (val) => {
      store.set('forcedView', val);
      if (refs.viewSelect) refs.viewSelect.value = val;
      if (refs.menuViewSelect) refs.menuViewSelect.value = val;
      syncCustomSelect(refs.viewSelect);
      syncCustomSelect(refs.menuViewSelect);
    };
    if (refs.viewSelect) refs.viewSelect.addEventListener('change', (e) => onViewChange(e.target.value));
    if (refs.menuViewSelect) refs.menuViewSelect.addEventListener('change', (e) => onViewChange(e.target.value));

    // Listen for iframe navigation changes or crash errors
    refs.previewIframe.addEventListener('load', () => {
      // Bypassed if we are in cross-origin sandbox mode since location properties are blocked by design.
      if (getSandboxUrl()) return;

      try {
        const href = refs.previewIframe.contentWindow.location.href;
        // If the location has changed away from our sandboxed document stream (e.g. to about:blank or an error page)
        if (href && href !== 'about:srcdoc' && !href.startsWith('about:srcdoc#')) {
          this.console.append('error', `[Navigation Error] Preview iframe loaded an external or system URL: ${href}`);
        }
      } catch (err) {
        // A cross-origin or secure browser error page (like about:neterror) was loaded!
        this.console.append('error', `[Navigation Error] Preview iframe failed to load or encountered a blocked navigation. If you clicked a link, make sure it is a valid hash route or use Standalone Export to test external links.`);
      }
    });

    // Live preview toggle
    if (refs.liveBtn) refs.liveBtn.addEventListener('click', () => setLive(!store.get('liveEnabled')));

    // Scratch pad open/close from menu
    if (refs.menuScratchBtn) {
      refs.menuScratchBtn.addEventListener('click', () => {
        this.scratch.toggle();
        closeMenu();
      });
    }
  }

  configureFeatureServices() {
    const consoleView = this.console;
    configureRunner({
      console: consoleView,
      isMobile: () => this.isMobile(),
      setActiveMobileTab: (name) => this.setActiveMobileTab(name),
      restartPreviewSandbox: () => this.startPreviewSandbox(),
      onJsRunStateChange: () => refreshJsPendingState()
    });
    configureExport({ console: consoleView });
    configureLiveReflection({ console: consoleView });
    configureTemplateLoader({ console: consoleView });
  }

  configurePreviewBridge() {
    store.subscribe('activePreviewRoute', (route) => {
      this.activePreviewRoute = route;
    });

    previewBridge.start({
      onActiveRoute: (data) => {
        this.activePreviewRoute = data.hash;
        store.set('activePreviewRoute', data.hash);
      },
      onConsole: (data) => {
        this.console.append(data.level, data.message, data);
      },
      onInspectResult: (data) => {
        handlePreviewInspectResult(data);
      },
      onPreviewContextMenu: (data) => {
        handlePreviewContextMenuMessage(data);
      },
      onPreviewNavigate: (data) => {
        this.handlePreviewNavigation(data.href, data.fromPath);
      },
      onPreviewRenderComplete: (data) => {
        finishPreviewTimingById(data.timingId);
        handlePreviewRenderComplete(data.timingId);
      },
      onPreviewScroll: (data) => {
        handlePreviewScroll(data.x, data.y);
      },
      onSandboxReady: () => {
        if (this.sandboxReadyTimer) {
          window.clearTimeout(this.sandboxReadyTimer);
          this.sandboxReadyTimer = null;
        }
      },
      onPreviewEscape: () => {
        disableInspector();
        panelSystem.exitFocus();
      }
    });
  }

  resolvePreviewNavigation(href, fromPath = 'index.html') {
    if (!href || typeof href !== 'string') {
      return { ok: false, reason: 'empty' };
    }

    if (/^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith('//')) {
      return { ok: false, reason: 'external' };
    }

    let url;
    try {
      url = new URL(href, `https://front-my-end.local/${fromPath || 'index.html'}`);
    } catch (err) {
      return { ok: false, reason: 'invalid' };
    }

    const path = decodeURIComponent(url.pathname.replace(/^\/+/, '')) || 'index.html';
    const normalizedPath = path.endsWith('/') ? `${path}index.html` : path;
    const route = url.hash || null;

    if (normalizedPath === 'index.html') {
      return { ok: true, path: normalizedPath, route };
    }

    return { ok: false, reason: 'missing', path: normalizedPath, route };
  }

  handlePreviewNavigation(href, fromPath) {
    const result = this.resolvePreviewNavigation(href, fromPath);

    if (result.ok) {
      store.set('activePreviewPath', result.path);
      store.set('activePreviewRoute', result.route);
      void runCode({ trigger: RUN_TRIGGERS.IMPLICIT, resetScroll: true });
      if (result.route) {
        this.console.system(`Preview navigated to ${result.path}${result.route}.`);
      } else {
        this.console.system(`Preview navigated to ${result.path}.`);
      }
      return;
    }

    if (result.reason === 'missing') {
      this.console.append('warn', `Preview page not found: ${result.path}. Multi-page navigation is isolated and only virtual pages known to the editor can be rendered.`);
      return;
    }

    this.console.append('warn', `Preview navigation blocked: ${href}.`);
  }

  // ---- Import (Goal 12) -----------------------------------------------------
  registerImport() {
    if (refs.importBtn) refs.importBtn.addEventListener('click', () => this.openFilePicker());
    if (refs.fileInput) {
      refs.fileInput.addEventListener('change', (e) => {
        this.handleFiles(e.target.files);
        // Reset so selecting the same file again re-triggers change.
        refs.fileInput.value = '';
      });
    }
    this.registerDragAndDrop();
  }

  openFilePicker() {
    if (refs.fileInput) refs.fileInput.click();
  }

  registerDragAndDrop() {
    const overlay = refs.dropOverlay;
    let depth = 0;

    const show = () => { if (overlay) overlay.classList.add('active'); };
    const hide = () => { if (overlay) overlay.classList.remove('active'); };

    window.addEventListener('dragenter', (e) => {
      if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes('Files')) return;
      e.preventDefault();
      depth++;
      show();
    });
    window.addEventListener('dragover', (e) => {
      if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes('Files')) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    });
    window.addEventListener('dragleave', (e) => {
      e.preventDefault();
      depth = Math.max(0, depth - 1);
      if (depth === 0) hide();
    });
    window.addEventListener('drop', (e) => {
      if (!e.dataTransfer) return;
      e.preventDefault();
      depth = 0;
      hide();
      if (e.dataTransfer.files && e.dataTransfer.files.length) {
        this.handleFiles(e.dataTransfer.files);
      }
    });
  }

  async handleFiles(fileList) {
    let result;
    try {
      result = await importFiles(fileList);
    } catch (_e) {
      const message = document.createElement('p');
      message.textContent = 'Something went wrong while reading the file(s). Please try again.';
      Modal.showMessage('Import failed', message);
      return;
    }

    const { patch, errors, notes } = result;

    if (Object.prototype.hasOwnProperty.call(patch, 'html')) {
      refs.htmlEditor.value = patch.html;
      store.set('html', patch.html);
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'css')) {
      refs.cssEditor.value = patch.css;
      store.set('css', patch.css);
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'js')) {
      refs.jsEditor.value = patch.js;
      store.set('js', patch.js);
    }

    if (Object.keys(patch).length) {
      triggerAllHighlights();
      void runCode({ trigger: RUN_TRIGGERS.EXPLICIT });
      notes.forEach((n) => this.console.system(n));
      setTemplateSelectValue('empty');
    }

    if (errors.length) {
      this.showImportErrors(errors);
      errors.forEach((err) => this.console.append('error', err));
    }
  }

  showImportErrors(errors) {
    const intro = document.createElement('p');
    intro.textContent = "Some files couldn't be imported:";

    const list = document.createElement('ul');
    list.className = 'message-list';
    errors.forEach((error) => {
      const item = document.createElement('li');
      item.textContent = error;
      list.appendChild(item);
    });

    const guidelines = document.createElement('p');
    guidelines.className = 'message-guidelines';
    const strong = document.createElement('strong');
    strong.textContent = 'Accepted formats:';
    guidelines.appendChild(strong);
    guidelines.append(' .html, .css, or .js.');
    guidelines.appendChild(document.createElement('br'));
    guidelines.append(`We only accept text/code files up to ${formatBytes(MAX_BYTES)} each.`);

    Modal.showMessage('Import notice', [intro, list, guidelines]);
  }

  // ---- State ----------------------------------------------------------------
  async restoreSavedState() {
    // Theme — default to Retro Light on first load
    const savedTheme = Storage.getTheme();
    const theme = savedTheme || 'light-mode';
    store.set('activeTheme', theme);

    // Code
    const { html, css, js } = Storage.getCode();
    if (html !== null || css !== null || js !== null) {
      refs.htmlEditor.value = html || '';
      refs.cssEditor.value = css || '';
      refs.jsEditor.value = js || '';

      store.set('html', html || '');
      store.set('css', css || '');
      store.set('js', js || '');

      this.console.system('Workspace restored from browser session.');
      // Boot restore is implicit: it renders the last explicitly-run JS, and
      // skips a workspace whose previous render never finished (safe boot).
      await runCode({ trigger: RUN_TRIGGERS.IMPLICIT, boot: true });
      if (isJsPending()) {
        this.console.system('JavaScript changed since your last run, so it was not run automatically. Press Run to execute it.');
      }
    } else {
      await loadTemplate('counter');
    }

    // Live preview preference
    store.set('liveEnabled', Storage.getLive());

    // Layout View Override
    const savedView = Storage.getView();
    const forcedView = savedView || 'auto';
    store.set('forcedView', forcedView);
    if (refs.viewSelect) refs.viewSelect.value = forcedView;
    if (refs.menuViewSelect) refs.menuViewSelect.value = forcedView;
    applyResponsiveMode();
  }
}

// Instantiate and start
let app = null;
try {
  app = new FiddleApp();
  // Dev/debug escape hatch; app modules receive explicit services from FiddleApp.
  window.__app = app;
} catch (error) {
  if (error && typeof error === 'object') error.startupPhase = 'construct';
  document.addEventListener('DOMContentLoaded', () => showStartupFailure(error));
}

document.addEventListener('DOMContentLoaded', () => {
  if (!app) return;
  app.start().catch((err) => {
    showStartupFailure(err);
  });
});
